import React, { useState, useEffect, useMemo, useRef } from 'react';
import Tournaments, { TournamentCard } from './Tournaments';
import Home from './Home';
import Leaderboard from './Leaderboard';
import styles from "../assets/style.module.css";
import { i18nManager } from '../../../i18n';
import { IMember, IProgram } from '../../../services';
import * as ReactRouterDom from "react-router-dom";
import { Classname, ColorHelper } from '@rivtn/noseur';
import { useComponentStyle } from '../../../hooks/useComponentStyle';
import { CacheManager, Utils } from '../../../utils';

const UserAccountMenu = ({ onSignOut }: { onSignOut: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const profile = CacheManager.get(CacheManager.PROFILE_KEY) as IMember;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={styles["dropdown-container"]} ref={menuRef}>
            <div className={styles["user-trigger"]} onClick={() => setIsOpen(!isOpen)}>
                <img className={styles["user-avatar"]} src={profile.profile_picture_url} alt={profile.username} />
                <div className={styles["user-name"]}>{profile.username}</div>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </div>
            {isOpen && (
                <div className={styles["dropdown-menu"]}>
                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}`} className={({ }) => Classname.build(styles["dropdown-item"])}>📊 Dashboard</ReactRouterDom.NavLink>
                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/tournaments?section=REGISTERED`} className={({ }) => Classname.build(styles["dropdown-item"])}>🏆 My Tournaments</ReactRouterDom.NavLink>
                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_SUPPORT_URL}`} className={({ }) => Classname.build(styles["dropdown-item"])}>🎧 Support</ReactRouterDom.NavLink>
                    <div style={{ height: '1px', background: 'var(--border-color)', margin: '8px 0' }} />
                    <div
                        className={`${styles["dropdown-item"]} ${styles["danger"]}`}
                        onClick={() => { onSignOut(); setIsOpen(false); }}
                    >
                        🚪 Sign Out
                    </div>
                </div>
            )}
        </div>
    );
};

export interface LandingProps {
    program: IProgram;
    basePath: string;
}

function Landing(props: LandingProps) {
    const now = new Date();
    const labels = i18nManager.Labels;
    const { program, basePath } = props;
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const colorRgba = ColorHelper.hexToRgba(program.extra_data?.color_hex ?? "#00f2ff");
    const [profile, setProfile] = React.useState<IMember | undefined>(CacheManager.get(CacheManager.PROFILE_KEY) as IMember);
    useComponentStyle(`
        :root {
            --accent-color: ${program.extra_data?.color_hex ?? "#00f2ff"};
            --accent-glow: rgba(${colorRgba.r}, ${colorRgba.g}, ${colorRgba.a}, 0.4);
        }
   `);

    React.useEffect(() => {
        Utils.updateMeta({
            title: program.full_name,
            logo: program.logo_urls?.[0],
            desc: labels.program.program,
        });
    });

    return (
        <div className={styles["app-wrapper"]}>
            <header className={styles["header"]}>
                <div className={`${styles["container"]} ${styles["nav-inner"]}`}>
                    <ReactRouterDom.NavLink end to={{ pathname: `/${basePath}`, search: searchParams.toString() }} className={styles[`logo`]}>
                        <img className={styles["logo-icon"]} alt={labels.omnehost} src={program.logo_urls?.[0] ?? labels.omnehost_logo_url} />
                        <span>{program.name} <span style={{ color: 'var(--accent-color)' }}> {(program.short_name !== program.name) && (<>({program.short_name})</>)}</span></span>
                    </ReactRouterDom.NavLink>
                    <nav className={styles["nav-links"]}>
                        <ReactRouterDom.NavLink end to={{ pathname: `/${basePath}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{labels.program.home_label}</ReactRouterDom.NavLink>
                        <ReactRouterDom.NavLink to={{ pathname: `/${basePath}/tournaments`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{labels.program.tournaments_label}</ReactRouterDom.NavLink>
                        <ReactRouterDom.NavLink to={{ pathname: `/${basePath}/leaderboard`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{labels.program.leaderboard}</ReactRouterDom.NavLink>
                    </nav>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {(!profile) ? (
                            <button
                                className={styles["btn-primary"]}
                                onClick={() => Utils.signIn()}
                                style={{ padding: '12px 28px' }}
                            >
                                {labels.auth.sign_in}
                            </button>
                        ) : (
                            <UserAccountMenu onSignOut={() => {
                                CacheManager.clear();
                                setProfile(undefined);
                            }} />
                        )}
                    </div>
                </div>
            </header>

            <main style={{ minHeight: '80vh' }}>
                <ReactRouterDom.Routes>
                    <ReactRouterDom.Route path="/" element={<Home program={program} />} />
                    <ReactRouterDom.Route path="/tournaments" element={<Tournaments program={program} />} />
                    <ReactRouterDom.Route path="/leaderboard" element={<Leaderboard program={program} />} />
                </ReactRouterDom.Routes>
            </main>

            <footer className={styles["footer"]}>
                <div className={styles["container"]}>
                    <div className={styles["footer-grid"]}>
                        <div>
                            <div className={styles["logo"]} style={{ marginBottom: '24px' }}>
                                <img className={styles["logo-icon"]} alt={labels.omnehost} src={labels.omnehost_logo_url} />
                                <span>{labels.omnehost}<span style={{ color: 'var(--accent-color)' }}></span></span>
                            </div>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
                                {labels.omnehost_detail}
                            </p>
                        </div>

                        <div>
                            <div className={styles["footer-title"]}>Platform</div>
                            <ul className={styles["footer-link-list"]}>
                                <li className={styles["footer-link"]}><ReactRouterDom.NavLink end to={{ pathname: `/${basePath}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`footer-link`], { [styles["active"]]: isActive })}>{labels.program.home_label}</ReactRouterDom.NavLink></li>
                                <li className={styles["footer-link"]}><ReactRouterDom.NavLink end to={{ pathname: `/${basePath}/tournaments`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`footer-link`], { [styles["active"]]: isActive })}>{labels.program.tournaments_label}</ReactRouterDom.NavLink></li>
                                <li className={styles["footer-link"]}><ReactRouterDom.NavLink end to={{ pathname: `/${basePath}/leaderboard`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`footer-link`], { [styles["active"]]: isActive })}>{labels.program.leaderboard}</ReactRouterDom.NavLink></li>
                            </ul>
                        </div>

                        <div>
                            <div className={styles["footer-title"]}>Account</div>
                            <ul className={styles["footer-link-list"]}>
                                {(!profile)
                                    ? (<li className={styles["footer-link"]}><ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_PATULCIUS_AUTH_URL ?? "http://127.0.0.1:3005/oauth"}>{labels.auth.sign_in}</ReactRouterDom.NavLink></li>)
                                    : (<>
                                        <li className={styles["footer-link"]}> <ReactRouterDom.NavLink target="_blank" to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}`}>{labels.auth.dashboard}</ReactRouterDom.NavLink></li>
                                    </>)
                                }
                                <li className={styles["footer-link"]}><ReactRouterDom.NavLink target="_blank" to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/teams`}>{labels.auth.manage_your_teams}</ReactRouterDom.NavLink></li>
                                <li className={styles["footer-link"]}><ReactRouterDom.NavLink target="_blank" to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/hosts`}>{labels.auth.create_tournament}</ReactRouterDom.NavLink></li>
                            </ul>
                        </div>

                        <div>
                            <div className={styles["footer-title"]}>{labels.omnehost}</div>
                            <ul className={styles["footer-link-list"]}>
                                {labels.footer.map((f) => <li key={f.label} className={styles["footer-link"]}><ReactRouterDom.NavLink key={f.label} target="_blank" to={f.location}>{f.label}</ReactRouterDom.NavLink></li>)}
                            </ul>
                        </div>
                    </div>

                    <div className={styles["footer-copyright"]}>{labels.program.copyrights.replaceAll("${copyright}", program.copyright)}</div>
                    <div className={styles["footer-bottom"]}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                            © {now.getFullYear()} {labels.omnehost}. {labels.all_right_reserved}
                        </div>
                        <div className={styles["footer-socials"]}>
                            {Object.entries(Utils.OMNEHOST_SOCIALS).map(([social, username]) => (
                                <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={styles["footer-social-btn"]}><i className={Utils.getSocialIcon(social)} /></ReactRouterDom.NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default Landing;